import { countryCodes } from '../../pages/analytics/Data/dropdown';

// The line above the playlist table. It lives here rather than in each of the three views that
// render it because the three used to hold byte-identical copies, and the market suffix below is
// the kind of wording that drifts apart the moment it is written out three times.

/**
 * Turns a country code into something a person reads, falling back to the code itself for one our
 * list does not cover.
 */
const countryName = (code: string): string =>
  (countryCodes as Record<string, string>)[code] || code;

/**
 * Describes what the table is showing, including which market when the result is scoped to one.
 *
 * The market matters because the backend applies a country itself on Apple Music and Amazon, where
 * every playlist is published once per market and an unfiltered read is mostly the same handful of
 * playlists repeated. Without saying so, "Showing 226 playlists" reads as a worldwide count when it
 * is a US one — and the user never chose US, so they have no reason to suspect otherwise.
 *
 * With stale placements in the set it names both groups, "16 current · 4 not confirmed
 * recently", since the stale ones are listed in the table but left out of the count.
 *
 * @param total - Deduplicated placements in the whole set, not on this page
 * @param capped - The page cap stopped the walk early, so the set is incomplete
 * @param countryCode - The market the set is scoped to; null or absent means every market
 * @param staleCount - Stale placements listed but not counted in `total`
 * @param currentOnly - 1 on the Current tab, where every counted row is current
 */
export const playlistCountLabel = (
  total: number,
  capped: boolean,
  countryCode?: string | null,
  staleCount = 0,
  currentOnly: 0 | 1 = 1
): string => {
  const market = countryCode ? ` · ${countryName(countryCode)}` : '';

  if (capped) {
    return `Showing first ${total} of an unknown total. Some placements may be missing.${market}`;
  }

  if (staleCount > 0) {
    // The Historical tab also counts placements that have ended, so they are not "current".
    const counted = currentOnly === 1 ? `${total} current` : `${total} ${total === 1 ? 'playlist' : 'playlists'}`;
    return `${counted} · ${staleCount} not confirmed recently${market}`;
  }

  return `Showing ${total} ${total === 1 ? 'playlist' : 'playlists'}${market}`;
};
